import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { AudioProgressBar } from '@/components/AudioProgressBar';
import { YouTubePlayer } from '@/components/YouTubePlayer';
import { AudioPermissionDialog } from '@/components/AudioPermissionDialog';
import { useAudioPlayback } from '@/hooks/useAudioPlayback';
import { useTrackQueue } from '@/hooks/useTrackQueue';
import { useToast } from '@/hooks/use-toast';

interface Track {
  id: string;
  name: string;
  artist: string;
  album: string;
  imageUrl: string;
  previewUrl: string | null;
  spotifyUrl: string;
  uri: string;
  durationMs?: number;
  youtubeId?: string;
}

interface AudioPlayerContextType {
  currentTrack: Track | null;
  isPlaying: boolean;
  isAISuggestion: boolean;
  playTrack: (track: Track, isAISuggestion?: boolean) => void;
  togglePlayPause: () => void;
  stopPlayback: () => void;
  addToQueue: (tracks: Track[]) => void;
  clearQueue: () => void;
  enableAutoPlay: (enabled: boolean) => void;
  isAutoPlayEnabled: boolean;
  queue: Track[];
  onAddToSelections?: (track: Track) => Promise<void>;
  setOnAddToSelections: (callback: (track: Track) => Promise<void>) => void;
  audioPermissionGranted: boolean;
  requestAudioPermission: () => void;
  setAudioPermissionGranted: (granted: boolean) => void;
  showAudioPermissionDialog: () => void;
  currentTime: number;
  duration: number;
  progress: number;
  playbackMode: 'html5' | 'youtube' | 'unknown';
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
};

interface AudioPlayerProviderProps {
  children: React.ReactNode;
}

export const AudioPlayerProvider: React.FC<AudioPlayerProviderProps> = ({ children }) => {
  const { toast } = useToast();
  const [isAISuggestion, setIsAISuggestion] = useState(false);
  const [isAutoPlayEnabled, setIsAutoPlayEnabled] = useState(false);
  const [onAddToSelections, setOnAddToSelections] = useState<((track: Track) => Promise<void>) | undefined>(undefined);
  const [audioPermissionGranted, setAudioPermissionGranted] = useState(() => {
    const stored = localStorage.getItem('audio_permission_granted');
    return stored === 'true';
  });
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);

  // Use the unified audio playback hook (HTML5 + YouTube fallback)
  const {
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    progress,
    playbackMode,
    playTrack: playAudioTrack,
    togglePlayPause,
    stopPlayback: stopAudioPlayback,
    handleProgress,
  } = useAudioPlayback();

  // Use the queue hook for queue management
  const {
    queue,
    addToQueue,
    clearQueue,
    playNextInQueue,
  } = useTrackQueue({
    isAutoPlayEnabled,
    currentTrack,
    onPlayNext: (track) => playAudioTrack(track),
  });

  // Wrapper for playTrack to handle AI suggestion flag
  const playTrack = useCallback(async (track: Track, isAISuggestion = false) => {
    setIsAISuggestion(isAISuggestion);
    await playAudioTrack(track);
  }, [playAudioTrack]);

  // Wrapper for stopPlayback to clear AI suggestion flag
  const stopPlayback = useCallback(() => {
    setIsAISuggestion(false);
    stopAudioPlayback();
  }, [stopAudioPlayback]);

  const enableAutoPlay = useCallback((enabled: boolean) => {
    setIsAutoPlayEnabled(enabled);
  }, []);

  const setOnAddToSelectionsCallback = useCallback((callback: (track: Track) => Promise<void>) => {
    setOnAddToSelections(() => callback);
  }, []);

  const requestAudioPermission = useCallback(() => {
    setAudioPermissionGranted(true);
    localStorage.setItem('audio_permission_granted', 'true');
    console.log('✅ Audio permission granted by user');
  }, []);

  const handleSetAudioPermissionGranted = useCallback((granted: boolean) => {
    setAudioPermissionGranted(granted);
    localStorage.setItem('audio_permission_granted', granted ? 'true' : 'false');
  }, []);

  const showPermissionDialog = useCallback(() => {
    setIsPermissionDialogOpen(true);
  }, []);

  // Expose showPermissionDialog globally so useAudioPlayback can access it
  useEffect(() => {
    (window as any).__audioPlayerShowPermissionDialog = showPermissionDialog;
    return () => {
      delete (window as any).__audioPlayerShowPermissionDialog;
    };
  }, [showPermissionDialog]);

  const value: AudioPlayerContextType = useMemo(() => ({
    currentTrack,
    isPlaying,
    isAISuggestion,
    playTrack,
    togglePlayPause,
    stopPlayback,
    addToQueue,
    clearQueue,
    enableAutoPlay,
    isAutoPlayEnabled,
    queue,
    onAddToSelections,
    setOnAddToSelections: setOnAddToSelectionsCallback,
    audioPermissionGranted,
    requestAudioPermission,
    setAudioPermissionGranted: handleSetAudioPermissionGranted,
    showAudioPermissionDialog: showPermissionDialog,
    currentTime,
    duration,
    progress,
    playbackMode,
  }), [currentTrack, isPlaying, isAISuggestion, playTrack, togglePlayPause, stopPlayback, addToQueue, clearQueue, enableAutoPlay, isAutoPlayEnabled, queue, onAddToSelections, setOnAddToSelectionsCallback, audioPermissionGranted, requestAudioPermission, handleSetAudioPermissionGranted, showPermissionDialog, currentTime, duration, progress, playbackMode]);

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
      {playbackMode === 'youtube' && currentTrack?.youtubeId && (
        <YouTubePlayer
          key="youtube-player"
          videoId={currentTrack.youtubeId}
          isPlaying={isPlaying}
          maxDuration={30}
          onEnded={playNextInQueue}
          onProgress={handleProgress}
        />
      )}
      
      {/* Global Audio Permission Dialog */}
      <AudioPermissionDialog
        open={isPermissionDialogOpen}
        onAccept={() => {
          requestAudioPermission();
          setIsPermissionDialogOpen(false);
          toast({
            title: "Audio Enabled",
            description: "You can now play music previews!",
          });
        }}
        onDecline={() => {
          setIsPermissionDialogOpen(false);
          handleSetAudioPermissionGranted(false);
          toast({
            title: "Audio Disabled",
            description: "Music previews won't play. You can enable this later.",
            variant: "default",
          });
        }}
      />
    </AudioPlayerContext.Provider>
  );
};
